<template>
  <div class="container-fluid">
    <div class="row justify-content-center mt-2">
      <div class="col px-0">
        <div class="card">
          <div class="card-body">
            <div class="clearfix">
              <h2 class="float-left">Interviews by Final/Adjusted Score</h2>
              <h6 class="float-right mt-2" v-if="scoresByTestCount">For detailed analysis, please contact us at support@verensics.com</h6>
            </div>
            <p class="lead">Interview count: {{ scoresByTestCount }}</p>
            <div v-if="!scoresByTestCount" class="alert alert-light text-center" role="alert">No data</div>
            <PieChart v-if="scoresByTestCount && pieChartData && pieChartOptions" :data="pieChartData" :options="pieChartOptions" class="pie-chart-container" />
          </div>
        </div>
      </div>
    </div>
    <div class="row justify-content-center mt-2">
      <div class="col px-0">
        <div class="card">
          <div class="card-body">
            <div class="clearfix">
              <h2 class="float-left">Final/Adjusted Scores by Category</h2>
              <h6 class="float-right mt-2" v-if="scoresByCategoryCount">For detailed analysis, please contact us at support@verensics.com</h6>
            </div>
            <p class="lead">Interview count: {{ scoresByCategoryCount }}</p>
            <div v-if="!scoresByCategoryCount" class="alert alert-light text-center" role="alert">No data</div>
            <BarChart
              v-if="scoresByCategoryCount && barChartData && barChartOptions"
              :data="barChartData"
              :options="barChartOptions"
              class="bar-chart-container"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="row justify-content-center mb-4 d-print-none">
      <!-- some room at the bottom of the page -->
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PieChart from "@/components/charts/PieChart.vue";
import BarChart from "@/components/charts/BarChart.vue";

export default {
  components: {
    PieChart,
    BarChart,
  },
  props: {
    data: Object,
  },
  data() {
    return {
      pieChartData: null,
      pieChartOptions: null,

      barChartData: null,
      barChartOptions: null,
    };
  },
  computed: {
    ...mapGetters("static", ["reportOverallScores", "reportStatusCause"]),
    scoresByTestCount() {
      if (!this.data || !this.data.scoresByTest) return 0;
      return this.data.scoresByTest.count;
    },
    scoresByCategoryCount() {
      if (!this.data || !this.data.scoresByCategory) return 0;
      return this.data.scoresByCategory.count;
    },
  },
  created() {
    this.initPieChart();
    this.initBarChart();
  },
  watch: {
    data() {
      this.initPieChart();
      this.initBarChart();
    },
  },
  methods: {
    initPieChart() {
      if (!this.data) {
        this.pieChartData = null;
        this.pieChartOptions = null;
        return;
      }

      const labels = this.data.scoresByTest.labels;
      const data = this.data.scoresByTest.dataSets[0].data;
      const backgroundColor = [];

      this.data.scoresByTest.labels.forEach(label => {
        const color = this.getReportOverallScoreColor(label);
        backgroundColor.push(color);
      });

      this.pieChartData = {
        datasets: [{ data, backgroundColor }],
        labels,
      };

      this.pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,
            bottom: 20,
            left: 0,
            right: 0,
          },
        },
        legend: {
          position: "left",
          labels: {
            fontSize: 18,
          },
        },
        animation: {
          duration: 0,
        },
        hover: {
          animationDuration: 0,
        },
        responsiveAnimationDuration: 0,
        plugins: {
          datalabels: {
            color: () => {
              return "black";
            },
            font: {
              size: 18,
            },
            formatter: function(value, context) {
              if (value === 0) return "";
              var total = context.dataset.data.reduce((x, y) => x + y, 0);
              var percentage = ((value / total) * 100).toFixed(1);
              return `${percentage}%`;
            },
            anchor: "end",
            align: function(context) {
              var index = context.dataIndex;
              var value = context.dataset.data[index];
              var total = context.dataset.data.reduce((x, y) => x + y, 0);
              var percentage = ((value / total) * 100).toFixed(1);
              return percentage < 6 ? "end" : "start";
            },
            offset: function(context) {
              var index = context.dataIndex;
              var value = context.dataset.data[index];
              var total = context.dataset.data.reduce((x, y) => x + y, 0);
              var percentage = ((value / total) * 100).toFixed(1);
              return percentage < 6 ? -2 : 4;
            },
            display: function(context) {
              var index = context.dataIndex;
              return index <= 2 ? true : "auto";
            },
          },
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              var label = data.labels[tooltipItem.index] || "";
              if (label) {
                label += ": ";
              }
              label += data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] + " Interviews";
              return label;
            },
          },
        },
      };
    },
    initBarChart() {
      if (!this.data) {
        this.barChartData = null;
        this.barChartOptions = null;
        return;
      }

      const labels = this.data.scoresByCategory.labels;
      const datasets = this.data.scoresByCategory.dataSets;
      const backgroundColor = [];

      this.data.scoresByCategory.labels.forEach(label => {
        const color = this.getReportOverallScoreColor(label);
        backgroundColor.push(color);
      });

      this.data.scoresByCategory.dataSets.forEach(ds => {
        const color = this.getReportOverallScoreColor(ds.label);
        ds.backgroundColor = [];
        ds.data.forEach(() => ds.backgroundColor.push(color));
      });

      this.barChartData = { labels, datasets };

      this.barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "top",
          labels: {
            fontSize: 18,
          },
        },
        animation: {
          duration: 0,
        },
        hover: {
          animationDuration: 0,
        },
        responsiveAnimationDuration: 0,
        plugins: {
          datalabels: {
            color: () => {
              return "black";
            },
            font: {
              size: 18,
            },
            formatter: function(value) {
              // do not show value when less than 8 due to crowding
              if (value <= 9) return "";
              return `${value}%`;
            },
          },
          chartLegendPadding: {
            paddingBottom: 20,
          },
        },
        scales: {
          yAxes: [
            {
              stacked: true,
              ticks: {
                beginAtZero: true,
                max: 100,
                maxTicksLimit: 15,
                fontSize: 18,
              },
            },
          ],
          xAxes: [
            {
              stacked: true,
              ticks: {
                autoSkip: false,
                beginAtZero: true,
                fontSize: 18,
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              var label = data.labels[tooltipItem.index] || "";
              if (label) {
                label += ": ";
              }
              label += data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] + "%";
              return label;
            },
          },
        },
      };
    },
    getReportOverallScoreColor(value) {
      var reportOverallScore = this.reportOverallScores.find(r => r.code === value || r.displayName === value);
      var reportStatusCause = this.reportStatusCause.find(r => r.code === value || r.displayName === value);
      return reportOverallScore ? reportOverallScore.color : reportStatusCause ? reportStatusCause.color : "#182c59";
    },
    getLuma(hex) {
      var rrggbb = hex.substring(1);
      var rgb = parseInt(rrggbb, 16); // convert rrggbb to decimal
      var r = (rgb >> 16) & 0xff;
      var g = (rgb >> 8) & 0xff;
      var b = (rgb >> 0) & 0xff;
      var luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return luma;
    },
  },
};
</script>

<style lang="scss" scoped>
.pie-chart-container {
  position: relative;
}

.bar-chart-container {
  position: relative;
}

@media screen {
  .pie-chart-container {
    height: 400px;
    width: 900px;
  }
  .bar-chart-container {
    height: 500px;
    width: 900px;
  }
}
</style>
